import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import type { Annotation } from '../../annotation';
import type { InspectionDamage, VinInspectionItem } from '../inspectionTypes';
import type { InspectionTabKey } from '../inspectionTabs';
import { DEFAULT_INSPECTION_TAB } from '../inspectionTabs';
import { inspectionApi } from '../inspectionAPI';
import useAssetDownload from '../../../hooks/useAssetDownload';
import { useDamages } from '../../../hooks/useDamages';
import useImageModal from '../../../hooks/useImageModal';
import { useStillFrame } from '../../../hooks/useStillFrame';
import { getDamageImageFileGroupKey, getDamageImageUrl } from '../../../utils/damageGrouping';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';
import {
  areAnnotationsEqual,
  buildAnnotations,
  getDamageLookupImageUri,
  getDamageMetadata,
  hasUnsavedAnnotationChanges,
  isDamageDeleted,
} from '../utils/damageAnnotationUtils';
import { getDamageHistoryEntries, isDamageOnImage } from '../utils/annotationHistoryUtils';

type UseInspectionMediaSessionParams = {
  refetchInspections: () => Promise<unknown> | unknown;
};

export const useInspectionMediaSession = ({ refetchInspections }: UseInspectionMediaSessionParams) => {
  const [mediaInspection, setMediaInspection] = useState<VinInspectionItem | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [mediaSourceTab, setMediaSourceTab] = useState<InspectionTabKey>(DEFAULT_INSPECTION_TAB);
  const vehicleImageDamageLookupRef = useRef(0);

  const { modalState, openModal, closeModal, setModalState } = useImageModal();
  const { downloadAsset } = useAssetDownload();
  const { createStillFrame, data: stillFrameData, clearStillFrame } = useStillFrame(mediaInspection?.inspectionId || '');
  const { damages: mediaDamages, fetchDamages: fetchMediaDamages } = useDamages({
    inspectionId: mediaInspection?.inspectionId || '',
  });
  const [fetchDamagesByImageUri] = inspectionApi.useLazyGetDamagesByImageUriQuery();
  const [fetchAllDamages] = inspectionApi.useLazyGetDamagesQuery();

  const unsavedAnnotationChanges = useMemo(() => hasUnsavedAnnotationChanges(annotations), [annotations]);

  useEffect(() => {
    if (!modalState.imageData || !mediaInspection) {
      setAnnotations(previousAnnotations => (previousAnnotations.length ? [] : previousAnnotations));
      return;
    }

    // Vehicle images sync annotations via syncVehicleImageDamages.
    if (mediaSourceTab === 'vehicleImages') {
      return;
    }

    const imageFileGroupKey = getDamageImageFileGroupKey(modalState.imageData);
    const groupedDamagesFromFetch = mediaDamages.filter(
      (damage: InspectionDamage) =>
        !isDamageDeleted(damage) && getDamageImageFileGroupKey(damage) === imageFileGroupKey
    );
    const groupedDamagesFromImage = (modalState.imageData?.damages ?? []).filter(
      (damage: InspectionDamage) => !isDamageDeleted(damage)
    );
    const sourceDamages = groupedDamagesFromImage.length ? groupedDamagesFromImage : groupedDamagesFromFetch;
    const nextAnnotations = buildAnnotations(sourceDamages);

    setAnnotations(previousAnnotations =>
      areAnnotationsEqual(previousAnnotations, nextAnnotations) ? previousAnnotations : nextAnnotations
    );
  }, [modalState.imageData, mediaInspection, mediaSourceTab, mediaDamages]);

  useEffect(() => {
    if (mediaSourceTab === 'vehicleImages' || !mediaDamages?.length) {
      return;
    }

    setAnnotations(previousAnnotations =>
      previousAnnotations.map(oldAnnotation => {
        if (!String(oldAnnotation?.id ?? '').includes('new_')) {
          return oldAnnotation;
        }

        const matchedAnnotation = mediaDamages.find((damage: InspectionDamage) => {
          const damageMeta = getDamageMetadata(damage);
          return (
            oldAnnotation?.type === damage?.type &&
            Math.abs((oldAnnotation?.x || 0) - (damageMeta?.x || 0)) < 0.01 &&
            Math.abs((oldAnnotation?.y || 0) - (damageMeta?.y || 0)) < 0.01 &&
            Math.abs((oldAnnotation?.x1 || 0) - (damageMeta?.x1 || 0)) < 0.01 &&
            Math.abs((oldAnnotation?.y1 || 0) - (damageMeta?.y1 || 0)) < 0.01
          );
        });

        if (matchedAnnotation?.id) {
          return {
            ...oldAnnotation,
            id: matchedAnnotation.id,
            action: 'updated',
          };
        }

        return oldAnnotation;
      })
    );
  }, [mediaDamages, mediaSourceTab]);

  useEffect(() => {
    if (!stillFrameData?.data || modalState.type !== 'video') return;

    const nextImageData = {
      // When the backend reused an existing damage frame, these link the new
      // annotation to that persisted crop under /damages instead of copying a
      // fresh image. imageURI prefers the backend-provided /damages URL.
      imageFileId: stillFrameData.data?.imageFileId,
      damageId: stillFrameData.data?.damageId,
      imageURI: stillFrameData.data?.imageURI || stillFrameData.data?.imagePath?.url,
      videoFrameTime: stillFrameData.data?.videoFrameTime,
      title: stillFrameData.data?.viewName,
      damages: [],
    };

    openModal(nextImageData, 'image');
  }, [stillFrameData, modalState.type, openModal]);

  const syncVehicleImageDamages = useCallback(
    async (inspectionId: string, imageData?: InspectionMediaData, preferCachedValue = false) => {
      const imageURI = getDamageLookupImageUri(imageData);
      const currentImageURI = imageData?.imageURI;

      if (!inspectionId || !imageURI) {
        return [];
      }

      const lookupId = ++vehicleImageDamageLookupRef.current;
      const damagesByImageUri = await fetchDamagesByImageUri({ inspectionId, imageURI }, preferCachedValue).unwrap();

      if (lookupId !== vehicleImageDamageLookupRef.current) {
        return damagesByImageUri;
      }

      const visibleDamages = damagesByImageUri.filter((damage: InspectionDamage) => !isDamageDeleted(damage));
      const historyDamages = damagesByImageUri.filter(damage => getDamageHistoryEntries(damage).length > 0);
      const imageFileId =
        visibleDamages[0]?.imageFileId ?? historyDamages[0]?.imageFileId ?? damagesByImageUri[0]?.imageFileId;

      if (!visibleDamages.length) {
        setAnnotations(previousAnnotations => (previousAnnotations.length ? [] : previousAnnotations));
        setModalState(previousState => {
          if (!previousState.imageData || previousState.imageData.imageURI !== currentImageURI) {
            return previousState;
          }

          return {
            ...previousState,
            imageData: {
              ...previousState.imageData,
              imageFileId: imageFileId ?? previousState.imageData.imageFileId,
              // Keep deleted rows that still carry history so the side panel can render.
              damages: historyDamages,
            },
          };
        });

        return damagesByImageUri;
      }

      const nextAnnotations = buildAnnotations(visibleDamages);
      setAnnotations(previousAnnotations =>
        areAnnotationsEqual(previousAnnotations, nextAnnotations) ? previousAnnotations : nextAnnotations
      );

      setModalState(previousState => {
        if (!previousState.imageData || previousState.imageData.imageURI !== currentImageURI) {
          return previousState;
        }

        return {
          ...previousState,
          imageData: {
            ...previousState.imageData,
            imageFileId: imageFileId ?? previousState.imageData.imageFileId,
            // Active damages for the canvas; include deleted-with-history for the panel.
            damages: [...visibleDamages, ...historyDamages.filter(damage => isDamageDeleted(damage))],
          },
        };
      });

      return damagesByImageUri;
    },
    [fetchDamagesByImageUri, setModalState]
  );

  const openMedia = useCallback(
    (
      inspectionItem: VinInspectionItem,
      imageData?: InspectionMediaData,
      type?: InspectionMediaType,
      activeTab: InspectionTabKey = DEFAULT_INSPECTION_TAB
    ) => {
      setMediaInspection(inspectionItem);
      setMediaSourceTab(activeTab);
      openModal(imageData, type);

      if (activeTab === 'vehicleImages') {
        syncVehicleImageDamages(inspectionItem.inspectionId, imageData, true).catch(error => {
          console.error('Failed to fetch vehicle image damages', error);
        });
      }
    },
    [openModal, syncVehicleImageDamages]
  );

  const forceCloseMedia = useCallback(() => {
    closeModal();
    setAnnotations([]);
    clearStillFrame();
  }, [clearStillFrame, closeModal]);

  const closeMedia = useCallback(() => {
    if (!unsavedAnnotationChanges) {
      forceCloseMedia();
      return;
    }

    Modal.confirm({
      title: 'Unsaved Changes',
      content: 'You have unsaved annotation changes. Select Discard to cancel your changes or Continue to keep editing.',
      okText: 'Discard',
      cancelText: 'Continue',
      okButtonProps: {
        style: {
          backgroundColor: '#eb4e2c',
          borderColor: '#eb4e2c',
        },
      },
      onOk: forceCloseMedia,
    });
  }, [forceCloseMedia, unsavedAnnotationChanges]);

  const refreshAfterAnnotationChange = useCallback(async () => {
    if (!mediaInspection?.inspectionId) return;

    if (mediaSourceTab === 'vehicleImages' && modalState.imageData?.imageURI) {
      await syncVehicleImageDamages(mediaInspection.inspectionId, modalState.imageData, false);
      await refetchInspections();
      return;
    }

    const nextDamages = await fetchMediaDamages();
    const allDamages = mediaInspection?.inspectionId
      ? await fetchAllDamages(mediaInspection.inspectionId).unwrap()
      : nextDamages;

    setMediaInspection(previousInspection =>
      previousInspection
        ? {
            ...previousInspection,
            damages: nextDamages,
            damageCount: nextDamages.length,
          }
        : previousInspection
    );
    setModalState(previousState => {
      if (!previousState.imageData) {
        return previousState;
      }

      const imageDamages = allDamages.filter((damage: InspectionDamage) => isDamageOnImage(damage, previousState.imageData));
      const nextGroupDamages = imageDamages.filter((damage: InspectionDamage) => !isDamageDeleted(damage));
      const historyDamages = imageDamages.filter(
        (damage: InspectionDamage) => isDamageDeleted(damage) && getDamageHistoryEntries(damage).length > 0
      );
      const damagesForImage = [...nextGroupDamages, ...historyDamages];

      return {
        ...previousState,
        imageData: {
          ...previousState.imageData,
          imageFileId: damagesForImage[0]?.imageFileId ?? previousState.imageData.imageFileId,
          imageURI: nextGroupDamages[0]
            ? (getDamageImageUrl(nextGroupDamages[0]) ?? previousState.imageData.imageURI)
            : previousState.imageData.imageURI,
          damages: damagesForImage,
        },
      };
    });

    await refetchInspections();
  }, [
    fetchAllDamages,
    fetchMediaDamages,
    mediaInspection?.inspectionId,
    mediaSourceTab,
    modalState.imageData,
    refetchInspections,
    setModalState,
    syncVehicleImageDamages,
  ]);

  const handleImageResolved = useCallback(
    ({ imageFileId, imageURI }: { imageFileId: string; imageURI?: string }) => {
      // First annotation on a brand-new frame resolves its persisted
      // imageFileId; store it so further annotations on the same frame
      // link to the same crop instead of copying a duplicate image.
      setModalState(previousState =>
        previousState.imageData
          ? {
              ...previousState,
              imageData: {
                ...previousState.imageData,
                imageFileId: previousState.imageData.imageFileId ?? imageFileId,
                imageURI: imageURI ?? previousState.imageData.imageURI,
              },
            }
          : previousState
      );
    },
    [setModalState]
  );

  return {
    mediaInspection,
    modalState,
    annotations,
    setAnnotations,
    mediaSourceTab,
    createStillFrame,
    downloadAsset,
    openMedia,
    closeMedia,
    forceCloseMedia,
    refreshAfterAnnotationChange,
    handleImageResolved,
  };
};
